import { MaterialCommunityIcons } from '@expo/vector-icons';
import { DayIntervalUnit } from '@gripp/shared-logic';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { TextInput } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { ControlledSelect, DatePickerSelector, useFormContext } from '../forms';
import {
  ClearButtonProps,
  ReminderAlertInputs,
  ReminderAlertType,
} from './types';

type IssueReminderDropdownInputProps = {
  type: ReminderAlertType;
};

export const IssueReminderDropdownInput = ({
  type,
}: IssueReminderDropdownInputProps) => {
  const { setValue, getValues, control, formState: { errors } } = useFormContext();
  const { t } = useTranslation();

  const alertPlaceholder: Record<ReminderAlertType, { placeholder: string }> = {
    [ReminderAlertType.Date]: { placeholder: 'MM/DD/YYYY' },
    [ReminderAlertType.Days]: { placeholder: 'Number of days' },
    [ReminderAlertType.DayInterval]: { placeholder: 'Number of ____' },
  };

  const dayIntervalUnitItems = [
    {
      label: t('alerts.dateInterval.minutes'),
      value: DayIntervalUnit.Minutes,
    },
    {
      label: t('alerts.dateInterval.hours'),
      value: DayIntervalUnit.Hours,
    },
    {
      label: t('alerts.dateInterval.days'),
      value: DayIntervalUnit.Days,
    },
    {
      label: t('alerts.dateInterval.weeks'),
      value: DayIntervalUnit.Weeks,
    },
    {
      label: t('alerts.dateInterval.months'),
      value: DayIntervalUnit.Months,
    },
  ];

  const ClearButton = ({ inputType }: ClearButtonProps) => {
    return (
      <Pressable
        hitSlop={8}
        onPress={() => {
          setValue(inputType, undefined, { shouldDirty: true });
        }}
      >
        <MaterialCommunityIcons name="close" size={16} />
      </Pressable>
    );
  };

  const FormErrorText = ({ fieldName }: { fieldName: string }) => {
    const error = errors[fieldName];
    if (!error?.message) {
      return null;
    }
    
    return (
      <Text style={styles.errorText}>
        {error.message.toString()}
      </Text>
    );
  };

  const renderInputComponent = (type: ReminderAlertType) => {
    switch (type) {
      case ReminderAlertType.Date:
        return (
          <View style={[styles.inputRow, { marginBottom: 8 }]}>
            <View style={[styles.inputContainer]}>
              <View style={styles.dateInput}>
                <DatePickerSelector
                  name="onDate"
                  setValue={setValue}
                  control={control}
                  label={'Date:'}
                  placeholder="MM/DD/YYYY"
                  containerStyle={{
                    borderWidth: 0,
                    padding: 0,
                    paddingVertical: 4,
                    paddingBottom: 8,
                    justifyContent: 'center',
                    height: '100%',
                  }}
                  maxYears={5}
                  minDate={moment().add(1, 'day').format('YYYY-MM-DD')}
                />
              </View>
            </View>
          </View>
        );
      case ReminderAlertType.Days:
        return (
          <View style={styles.multipleInputContainer}>
            <View style={[styles.inputRow, { marginBottom: 8 }]}>
              <View style={[styles.inputContainer]}>
                <TextInput
                  value={getValues(ReminderAlertInputs.days)?.toString() || ''}
                  mode="flat"
                  underlineColorAndroid="transparent"
                  activeOutlineColor="transparent"
                  outlineColor="transparent"
                  underlineColor="transparent"
                  activeUnderlineColor="transparent"
                  style={styles.inputButton}
                  contentStyle={styles.inputContent}
                  placeholder={alertPlaceholder[type].placeholder}
                  keyboardType="numeric"
                  onChangeText={(num) =>
                    setValue(
                      ReminderAlertInputs.days,
                      num ? parseInt(num, 10) : undefined,
                      {
                        shouldDirty: true,
                        shouldValidate: true,
                      }
                    )
                  }
                />
                <ClearButton inputType={ReminderAlertInputs.days} />
              </View>
              {/* {!isPrimaryAlertInput && (
                <DeleteInputButton inputType={ReminderAlertInputs.days} />
              )} */}
            </View>
            <View style={[styles.inputRow]}>
              <View style={[styles.inputContainer]}>
                <View style={styles.dateInput}>
                  <DatePickerSelector
                    name="daysStartDate"
                    setValue={setValue}
                    control={control}
                    label={t('alerts.startDate')}
                    containerStyle={{
                      borderWidth: 0,
                      padding: 0,
                      paddingVertical: 4,
                      paddingBottom: 8,
                      justifyContent: 'center',
                      height: '100%',
                    }}
                    maxYears={5}
                    minDate={moment().format('YYYY-MM-DD')}
                  />
                </View>
              </View>
            </View>
          </View>
        );
      case ReminderAlertType.DayInterval:
        return (
          <View style={styles.multipleInputContainer}>
            <View style={[styles.inputRow, { marginBottom: 8 }]}>
              <View style={[styles.inputContainer, errors.dayInterval && styles.inputError]}>
                <TextInput
                  value={getValues(ReminderAlertInputs.dayInterval)?.toString() || ''}
                  mode="flat"
                  underlineColorAndroid="transparent"
                  activeOutlineColor="transparent"
                  outlineColor="transparent"
                  underlineColor="transparent"
                  activeUnderlineColor="transparent"
                  style={styles.inputButton}
                  contentStyle={styles.inputContent}
                  placeholder={alertPlaceholder[type].placeholder}
                  keyboardType="numeric"
                  onChangeText={(num) =>
                    setValue(
                      ReminderAlertInputs.dayInterval,
                      num ? parseInt(num, 10) : undefined,
                      {
                        shouldDirty: true,
                        shouldValidate: true,
                      }
                    )
                  }
                />
                <ClearButton inputType={ReminderAlertInputs.dayInterval} />
              </View>
              
            </View>
            <FormErrorText fieldName="dayInterval" />            
            <View style={[styles.inputRow, { marginBottom: 8, zIndex: 9999 }]}>
              <View style={[styles.inputContainer, { flex: 1, paddingHorizontal: 0 }, errors.dayIntervalUnit && styles.inputError]}>
                <ControlledSelect
                  name={ReminderAlertInputs.dayIntervalUnit}
                  control={control}
                  items={dayIntervalUnitItems}
                  listMode="SCROLLVIEW"
                  dropDownPickerProps={{
                    style: {
                      borderWidth: 0,
                      backgroundColor: 'transparent',
                      height: '100%',
                      minHeight: 'auto',
                    },
                    textStyle: styles.inputContent,
                    placeholder: t('alerts.onDayIntervalUnit', 'Select unit'),
                    placeholderStyle: styles.placeholderLabel,
                    dropDownContainerStyle: {
                      borderWidth: 1,
                      borderColor: Colors.role.borderEmphasis,
                      backgroundColor: Colors.role.surface,
                      marginTop: 4,
                      borderRadius: 8,
                      position: 'absolute',
                      top: '100%',
                    },
                  }}
                  label={''}
                />
              </View>
            </View>
            <FormErrorText fieldName="dayIntervalUnit" />
          </View>
        );

      default:
        return (
          <View>
            <Text style={styles.placeholderLabel}>Select a logic...</Text>
          </View>
        );
    }
  };

  return <View style={styles.container}>{renderInputComponent(type)}</View>;
};

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    ...Platform.select({
      web: {
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
      },
      ios: {},
      android: {},
    }),
  },
  inputRow: {
    flexDirection: 'row',
    ...Platform.select({
      web: {
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
      },
      ios: {},
      android: {},
    }),
  },
  inputContainer: {
    borderWidth: 1,
    borderColor: Colors.role.borderEmphasis,
    height: 52,
    flex: 1,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    ...Platform.select({
      web: {
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
      },
      ios: {},
      android: {},
    }),
  },
  multipleInputContainer: {
    flex: 1,
    ...Platform.select({
      web: {
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
      },
      ios: {},
      android: {},
    }),
  },
  dateInput: {
    flex: 1,
    height: '100%',
    ...Platform.select({
      web: {
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
      },
      ios: {},
      android: {},
    }),
  },
  inputButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 5,
    fontSize: 16,
    fontWeight: '400',
    backgroundColor: 'transparent',
    ...Platform.select({
      web: {
        borderWidth: 0,
      },
      ios: {},
      android: {},
    }),
    color: Colors.role.textStrong,
  },
  inputContent: {
    backgroundColor: 'transparent',
    paddingHorizontal: 0,
    paddingVertical: 0,
    paddingLeft: 4,
    margin: 0,
    fontSize: 16,
    fontWeight: '400',
    color: Colors.role.textStrong,
  },
  placeholderLabel: {
    color: Colors.role.fillMuted,
    fontSize: 16,
    fontWeight: '400',
    flex: 1,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 12,
  },
  inputError: {
    borderColor: Colors.red,
    borderWidth: 1,
  },
  errorText: {
    color: Colors.red,
    fontSize: 14,
    marginBottom: 8,
    marginLeft: 4,
  },
});
